import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {useNavigate} from '@github-ui/use-navigate'
import {
  GitPullRequestIcon,
  PaperAirplaneIcon,
  RepoIcon,
  ScreenFullIcon,
  SquareFillIcon,
  ThumbsdownIcon,
  ThumbsupIcon,
} from '@primer/octicons-react'
import {ActionList, Box, Button, IconButton, TextInput} from '@primer/react'
import {Dialog, KeybindingHint} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useMemo, useRef, useState} from 'react'

import {copilotChatHeaderButtonID} from '../../utils/constants'
import {COPILOT_PATH} from '../../utils/copilot-chat-helpers'
import type {CopilotChatRepo} from '../../utils/copilot-chat-types'
import {getCommands} from '../../utils/task-oriented-assistive/commands'
import {ChatMessage} from '../ChatMessage'
import {ChatScrollContainer} from '../ChatScrollContainer'
import CopilotAnimation, {CopilotAnimationType} from '../CopilotAnimation'
import {useEntitlement} from '../quota/EntitlementContext'
import styles from './CommandWindow.module.css'

const CommandWindowState = {
  Initial: 'initial',
  Complete: 'complete',
} as const

type CommandWindowState = (typeof CommandWindowState)[keyof typeof CommandWindowState]

// Component for task-oriented assistive prototype
export function CommandWindow(): JSX.Element | undefined {
  const state = useChatState()
  const {entryPointId, chatIsOpen} = state

  const manager = useChatManager()
  const thread = manager.getSelectedThread(state)

  const {reloadQuota} = useEntitlement()
  const navigate = useNavigate()

  const [windowState, setWindowState] = useState<CommandWindowState>(CommandWindowState.Initial)

  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedCommandIndex, setSelectedCommandIndex] = useState<number | undefined>(undefined)

  const [currentScope] = useState<CopilotChatRepo | undefined>(() => {
    // Eventually, we'll show assistive tasks on other scopes besides repos/pull-requests
    return state.currentTopic
  })
  const [currentTarget] = useState<{type: string; number: number} | undefined>(() => {
    if (!Array.isArray(state.context)) {
      return undefined
    }

    // Eventually, we'll show assistive tasks on other scopes besides repos/pull-requests
    const pullRequest = state.context.find(item => item.type === 'pull-request')
    if (!pullRequest) {
      return undefined
    }

    // TODO the PullRequestReference type is not what actually comes back from the server
    return pullRequest as unknown as {type: string; number: number}
  })

  const immersiveUrl = useMemo(() => {
    if (thread) {
      return `${COPILOT_PATH}/c/${thread.id}`
    } else if (currentScope) {
      return `${COPILOT_PATH}/r/${currentScope.ownerLogin}/${currentScope.name}`
    } else {
      return COPILOT_PATH
    }
  }, [thread, currentScope])

  const [inputValue, setInputValue] = useState<string>('')
  const filteredCommands = useMemo(() => {
    return inputValue === ''
      ? getCommands(currentTarget?.type)
      : getCommands(currentTarget?.type).filter(command =>
          command.name.toLowerCase().startsWith(inputValue.toLowerCase()),
        )
  }, [inputValue, currentTarget])

  const copilotMessage = useMemo(() => {
    return state.messages.findLast(m => m.role === 'assistant')
  }, [state.messages])

  const onDialogClose = useCallback(
    (gesture: string): void => {
      setWindowState(CommandWindowState.Initial)
      setInputValue('')

      manager.closeChat()
      if (gesture === 'escape') {
        const entryId = entryPointId ?? copilotChatHeaderButtonID
        const entryElement = document.getElementById(entryId)
        const scrollX = window.scrollX
        const scrollY = window.scrollY

        // Persist current scroll position by delaying focus
        setTimeout(() => {
          entryElement?.focus()
          window.scrollTo(scrollX, scrollY)
        }, 0)
      }
    },
    [manager, entryPointId],
  )

  const onCommandSelected = useCallback(
    async (selectedCommand: string) => {
      setInputValue(selectedCommand)

      // Find the predefined command by name to get the full prompt
      // Otherwise, use the current text as a custom command
      const content = filteredCommands.find(command => command.name === selectedCommand)?.prompt ?? selectedCommand

      const trimmedContent = content.trim()
      if (trimmedContent === '') return

      reloadQuota()

      // This dialog is intended to be focused on a single task, so clear any existing messages.
      if (thread) {
        await manager.clearThread(thread)
      }

      // The sendChatMessage call will block until we start streaming a response.
      // So we set the dialog state here to make sure we can show that streaming response.
      setWindowState(CommandWindowState.Complete)

      await manager.sendChatMessage({
        content,
        thread,
        references: state.currentReferences,
        topic: state.currentTopic,
        context: state.context,
        customInstructions: state.customInstructions,
      })

      // After the response completes, highlight the input to allow the user to modify the command.
      setTimeout(() => {
        inputRef?.current?.select()
        window.scrollTo(scrollX, scrollY)
      }, 0)
    },
    [
      filteredCommands,
      manager,
      reloadQuota,
      state.context,
      state.currentReferences,
      state.currentTopic,
      state.customInstructions,
      thread,
    ],
  )

  const onInputKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      const lastIndex = filteredCommands.length - 1

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (lastIndex < 0) {
            setSelectedCommandIndex(undefined)
          } else {
            setSelectedCommandIndex(prevIndex =>
              prevIndex === undefined ? 0 : prevIndex < lastIndex ? prevIndex + 1 : 0,
            )
          }
          break
        case 'ArrowUp':
          e.preventDefault()
          if (lastIndex < 0) {
            setSelectedCommandIndex(undefined)
          } else {
            setSelectedCommandIndex(prevIndex =>
              prevIndex === undefined ? 0 : prevIndex > 0 ? prevIndex - 1 : lastIndex,
            )
          }
          break
        case 'Enter':
          e.preventDefault()
          if (selectedCommandIndex === undefined && inputValue !== '') {
            await onCommandSelected(inputValue)
          } else if (selectedCommandIndex !== undefined && filteredCommands[selectedCommandIndex]) {
            await onCommandSelected(filteredCommands[selectedCommandIndex].name)
          }
          break
        default:
          break
      }
    },
    [filteredCommands, selectedCommandIndex, inputValue, onCommandSelected],
  )

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value
      setInputValue(value)
      setSelectedCommandIndex(value === '' || filteredCommands.length === 0 ? undefined : 0)
      setWindowState(CommandWindowState.Initial)
    },
    [filteredCommands],
  )

  const onContinueInChat = useCallback(() => {
    navigate(immersiveUrl)
  }, [immersiveUrl, navigate])

  const renderTokens = (): JSX.Element | undefined => {
    if (currentTarget) {
      return (
        // TODO as we support non-pr targets, handle them here
        <div className="fgColor-muted bgColor-muted px-2 py-1 rounded-2 f6">
          <GitPullRequestIcon className="mr-1 fgColor-open" size="small" />
          {currentTarget.number}
        </div>
      )
    }

    return undefined
  }

  const renderCommandList = (): JSX.Element | undefined => {
    if (windowState !== CommandWindowState.Initial) {
      return undefined
    }

    let headingText = 'Pull request'
    if (inputValue) {
      headingText = 'Results found'
    }
    if (filteredCommands.length === 0) {
      headingText = 'No results found'
    }

    return (
      <ActionList variant="full" className="my-2">
        <ActionList.Group>
          <ActionList.GroupHeading as="h4" className="ml-n2">
            {headingText}
          </ActionList.GroupHeading>
          {filteredCommands.length > 0 && (
            <>
              {filteredCommands.map((command, index) => {
                const isSelected = index === selectedCommandIndex
                return (
                  <ActionList.Item
                    className={clsx('p-2', isSelected ? styles.commandListItemSelected : '')}
                    key={command.name}
                    onSelect={async () => await onCommandSelected(command.name)}
                  >
                    <ActionList.LeadingVisual>{command.leadingVisual}</ActionList.LeadingVisual>
                    {command.name}
                    {isSelected && <ActionList.TrailingVisual>Ask Copilot</ActionList.TrailingVisual>}
                  </ActionList.Item>
                )
              })}
            </>
          )}
          {filteredCommands.length === 0 && (
            <ActionList.Item
              className={clsx('p-2', styles.commandListItemSelected)}
              key="custom-command"
              onSelect={async () => await onCommandSelected(inputValue)}
            >
              <ActionList.LeadingVisual>
                <PaperAirplaneIcon />
              </ActionList.LeadingVisual>
              {inputValue}
              <ActionList.TrailingVisual>Ask Copilot</ActionList.TrailingVisual>
            </ActionList.Item>
          )}
        </ActionList.Group>
      </ActionList>
    )
  }

  const renderCommandInput = (): JSX.Element => {
    const isLoading = state.isWaitingOnCopilot
    return (
      <div className="d-flex flex-column">
        <TextInput
          ref={inputRef}
          size="large"
          leadingVisual={renderTokens()}
          trailingVisual={
            <div className="d-flex flex-row">
              {isLoading && (
                <IconButton
                  variant="invisible"
                  icon={SquareFillIcon}
                  aria-label="Stop response"
                  className="fgColor-danger"
                  onClick={() => {}}
                />
              )}
              <div className={styles.copilotAvatar}>
                <CopilotAnimation
                  animationType={isLoading ? CopilotAnimationType.Thinking : CopilotAnimationType.Idle}
                  loopAnimation
                  mode="assistive"
                />
              </div>
            </div>
          }
          value={inputValue}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          disabled={isLoading}
          placeholder="Search for actions"
        />
      </div>
    )
  }

  const renderResponse = (): JSX.Element | undefined => {
    const isLoading = state.messagesLoading.state === 'loading'
    const isLoaded = state.messagesLoading.state === 'loaded'
    const isError = state.messagesLoading.state === 'error'

    return (
      <ChatScrollContainer>
        <Box
          className={styles.chatMessageWrapper}
          sx={{
            // HACK: targeting a child component via CSS modules doesn't work
            '.message-container': {
              padding: 0,
              '> :first-child': {
                // Hide the Copilot avatar in the message area
                display: 'none',
              },
            },
          }}
        >
          {isLoading && <>Loading...</>}
          {isError && <>Error...</>}
          {isLoaded && !isLoading && copilotMessage && (
            <ChatMessage
              key={copilotMessage.id}
              message={copilotMessage}
              isStreaming={copilotMessage.id === state.streamingMessage?.id}
              inputRef={inputRef}
              excludeFeedback
            />
          )}
          {state.streamingMessage && (
            <ChatMessage
              key={state.streamingMessage.id}
              message={state.streamingMessage}
              isStreaming
              inputRef={inputRef}
              excludeFeedback
            />
          )}
        </Box>
      </ChatScrollContainer>
    )
  }

  const renderHeader = (): JSX.Element => {
    return (
      // TODO as we support non-repo scopes, handle them here
      <>
        <RepoIcon className="mr-1" size="small" />
        <span className="flex-1">{`${currentScope?.ownerLogin}/${currentScope?.name}`}</span>
      </>
    )
  }

  const renderBody = (): JSX.Element | undefined => {
    if (windowState === CommandWindowState.Initial) {
      return renderCommandList()
    }

    if (windowState === CommandWindowState.Complete) {
      return renderResponse()
    }

    return undefined
  }

  const renderFooter = (): JSX.Element | undefined => {
    if (windowState === CommandWindowState.Initial) {
      return (
        <>
          <div>Select an action</div>
          <div>
            <Button className="d-inline-block fgColor-muted" variant="invisible" onClick={() => {}}>
              Confirm <KeybindingHint keys="Enter" format="full" />
            </Button>
          </div>
        </>
      )
    }

    if (windowState === CommandWindowState.Complete) {
      return (
        <>
          <div>
            <IconButton variant="invisible" icon={ThumbsupIcon} aria-label="Positive" />
            <IconButton variant="invisible" icon={ThumbsdownIcon} aria-label="Negative" />
            <CopyToClipboardButton textToCopy={copilotMessage?.content ?? ''} ariaLabel="Copy to clipboard" />
          </div>
          <div>
            <Button className="d-inline-block fgColor-muted" variant="invisible" onClick={onContinueInChat}>
              Continue in Chat <KeybindingHint keys="Mod+C" format="condensed" />
            </Button>
          </div>
        </>
      )
    }

    return undefined
  }

  if (!chatIsOpen) {
    return undefined
  }

  return (
    <Dialog
      className={styles.dialog}
      initialFocusRef={inputRef}
      renderHeader={() => {
        return (
          <Dialog.Header className="d-flex flex-row flex-justify-between flex-items-center px-4 pt-2 mr-n3 box-shadow-none">
            <div className="fgColor-muted">{renderHeader()}</div>
            <div data-testid="copilot-assistive-command-window" />

            <div>
              <IconButton
                size="small"
                variant="invisible"
                icon={ScreenFullIcon}
                aria-label="Take conversation to immersive"
                disabled={state.isWaitingOnCopilot}
                onClick={onContinueInChat}
              />
              <Dialog.CloseButton onClose={() => onDialogClose('close-button')} />
            </div>
          </Dialog.Header>
        )
      }}
      renderBody={() => {
        return (
          <Dialog.Body className="py-1">
            {renderCommandInput()}
            {renderBody()}
          </Dialog.Body>
        )
      }}
      renderFooter={() => {
        // There's no footer while waiting for Copilot's response
        if (state.isWaitingOnCopilot) {
          return undefined
        }

        return (
          <Dialog.Footer className="d-flex flex-row flex-justify-between flex-items-center px-3 py-2 fgColor-muted">
            {renderFooter()}
          </Dialog.Footer>
        )
      }}
      onClose={onDialogClose}
    />
  )
}

try{ CommandWindow.displayName ||= 'CommandWindow' } catch {}